import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useContext, useMemo, useState } from "react";
import { TextAreaInput } from "../../Helpers/Input/Input";
import trans, { isRTL } from "../../../utils";
import Button from "../../Helpers/Button/Button";
import { popupcontext } from "../../../context/Popupscontext";
import {
  useGetIRenumsQuery,
  useQaIRMutation,
} from "../../../Redux/service/IR/IR";
import { toast } from "react-toastify";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import Loader from "../../Helpers/Loader/Loader";
import FileUploader from "../../Helpers/Dropzone/FileUploader";

export default function InspectionRequestQA({
  deficienciesLen,
  setQAAction,
  refetch,
}) {
  const initialValues = {
    result: null,
    comments: null,
    deficiencies: [],
  };

  const [AddQA, { isLoading }] = useQaIRMutation();
  let { currentIRId, setReloadData, setcurrentIRId } = useContext(popupcontext);
  const [data, setData] = useState([]);
  const { data: enums } = useGetIRenumsQuery();

  const { deficiencySeverity } = useMemo(() => {
    if (!enums?.data) return { deficiencySeverity: [] };

    const { deficiencySeverity } = enums.data;
    const convertToOptions = (obj) =>
      Object.entries(obj).map(([key, value]) => ({
        label: trans(value),
        value: value,
      }));

    return {
      deficiencySeverity: convertToOptions(deficiencySeverity),
    };
  }, [enums]);

  const categoryQA = [
    { label: trans("Workmanship"), value: "WORKMANSHIP" },
    { label: trans("Dimensional"), value: "DIMENSIONAL" },
    { label: trans("Material"), value: "MATERIAL" },
    { label: trans("Installation"), value: "INSTALLATION" },
    { label: trans("Documentation"), value: "DOCUMENTATION" },
    { label: trans("Safety"), value: "SAFETY" },
    {
      label: trans("Testing & Commissioning"),
      value: "TESTING_AND_COMMISSIONING",
    },
    { label: trans("Design/Coordination"), value: "DESIGN_COORDINATION" },
    { label: trans("Non-Compliance"), value: "NON_COMPLIANCE" },
    { label: trans("Incomplete/Rework"), value: "INCOMPLETE_REWORK" },
  ];

  const validationSchema = Yup.object({
    result: Yup.string().required(trans("Result is required")),
    comments: Yup.string().nullable(),
    deficiencies: Yup.array().of(
      Yup.object({
        category: Yup.string().nullable(),
        severity: Yup.string().nullable(),
        description: Yup.string().nullable(),
        files: Yup.array().nullable(),
      })
    ),
  });

  const addQA = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (val) => {
      try {
        if (addQA.values.result === "REJECTED") {
          const hasEmptyFields = data.some(
            (i) => !i.category || !i.severity || !i.description
          );

          if (hasEmptyFields) {
            toast.error(trans("Please fill all deficiency data"), {
              autoClose: 800,
              rtl: localStorage.getItem("lang") === "ar",
            });
            return;
          }

          // Create FormData for file uploads
          const formData = new FormData();
          formData.append("result", val.result);
          if (val.comments) {
            formData.append("comments", val.comments);
          }

          // Add deficiencies in the exact format: deficiencies[0][category], etc.
          data.forEach((deficiency, index) => {
            formData.append(
              `deficiencies[${index}][category]`,
              deficiency.category
            );
            formData.append(
              `deficiencies[${index}][description]`,
              deficiency.description
            );
            formData.append(
              `deficiencies[${index}][severity]`,
              deficiency.severity
            );

            // Add files if they exist
            if (deficiency.files && deficiency.files.length > 0) {
              deficiency.files.forEach((file) => {
                formData.append(`deficiencies[${index}][files][]`, file);
              });
            }
          });

          // Submit with FormData
          await AddQA({ id: currentIRId, val: formData }).unwrap();
          setQAAction(false);
          toast.success(trans("IR QA Review Submitted Successfully"), {
            autoClose: 800,
            rtl: localStorage.getItem("lang") === "ar",
          });
          refetch();
          setReloadData(true);
        } else {
          // For APPROVED, send regular JSON
          await AddQA({ id: currentIRId, val }).unwrap();
          toast.success(trans("IR QA Review Submitted Successfully"), {
            autoClose: 800,
            rtl: localStorage.getItem("lang") === "ar",
          });
          setQAAction(false);
          refetch();
          setReloadData(true);
        }
      } catch (error) {
        console.log("errr", error);
        toast.error(`${error?.data?.message}`, {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      }
    },
  });

  // Handle file selection for a specific deficiency using FileUploader
  const handleFileChange = (index, files) => {
    setData((prev) =>
      prev.map((item, i) => (i === index ? { ...item, files: files } : item))
    );
  };

  if (isLoading)
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );

  return (
    <form onSubmit={addQA.handleSubmit}>
      {/* Hide comments when deficiencies are added */}
      {data.length === 0 && (
        <TextAreaInput
          title={trans("Comments")}
          rows={3}
          placeholder={trans("Enter your comments")}
          id="comments"
          name="comments"
          value={addQA.values.comments}
          onChange={addQA.handleChange}
          onBlur={addQA.handleBlur}
          error={addQA.touched.comments && addQA.errors.comments}
        />
      )}

      <div
        style={{
          maxHeight: "250px",
          overflowY: "scroll",
          overflowX: "none",
          marginBlock: "10px",
        }}
      >
        {data?.map((def, index) => (
          <div key={index} className="row align-items-end mb-3">
            <div className="col-lg-5">
              <InputSelect
                label={trans("Category")}
                options={categoryQA}
                placeholder={trans("Select Category")}
                value={data[index].category}
                onChange={(value) =>
                  setData((prev) =>
                    prev.map((item, i) =>
                      i === index ? { ...item, category: value } : item
                    )
                  )
                }
              />
            </div>
            <div className="col-lg-5">
              <InputSelect
                label={trans("Severity")}
                options={deficiencySeverity}
                placeholder={trans("Select Severity")}
                value={data[index].severity}
                onChange={(value) =>
                  setData((prev) =>
                    prev.map((item, i) =>
                      i === index ? { ...item, severity: value } : item
                    )
                  )
                }
              />
            </div>
            <div className="col-lg-10 mt-2">
              <TextAreaInput
                title={trans("Description")}
                rows={3}
                placeholder={trans("Enter description")}
                name={`deficiencies[${index}].description`}
                value={data[index].description}
                onChange={(e) =>
                  setData((prev) =>
                    prev.map((item, i) =>
                      i === index
                        ? { ...item, description: e.target.value }
                        : item
                    )
                  )
                }
                onBlur={addQA.handleBlur}
              />
            </div>

            {/* File Upload Section - Using FileUploader Component */}
            <div className="col-lg-10 mt-2">
              <FileUploader
                file={data[index].files || []}
                onFileSelect={(files) => handleFileChange(index, files)}
                multiple={true}
                sizeMB={5}
                label={trans("Upload Attachments")}
              />
            </div>

            <div className="col-lg-2 mt-2 text-end">
              <Button
                type="default"
                danger
                text={trans("Remove")}
                onClick={() =>
                  setData((prev) => prev.filter((_, i) => i !== index))
                }
              />
            </div>
          </div>
        ))}

        <Button
          type="dashed"
          text={trans("Add Deficiency")}
          className="mt-2"
          onClick={() =>
            setData((prev) => [
              ...prev,
              {
                category: null,
                description: null,
                severity: null,
                files: [],
              },
            ])
          }
        />
      </div>

      <div className="d-flex align-items-center mt-4">
        <Button
          htmlType="submit"
          type="primary"
          text={trans("Approve")}
          onClick={() => addQA.setFieldValue("result", "APPROVED")}
          disabled={data?.length > 0}
          className={!isRTL ? "me-2" : "ms-2"}
        />

        <Button
          htmlType="submit"
          danger
          text={trans("Reject")}
          onClick={() => addQA.setFieldValue("result", "REJECTED")}
          disabled={data?.length === 0}
        />
        {deficienciesLen > 0 && (
          <Button
            htmlType="button"
            type="default"
            text={trans("Cancel")}
            onClick={() => setQAAction(false)}
            className={isRTL ? "me-2" : "ms-2"}
          />
        )}
      </div>
      {addQA.touched.result && addQA.errors.result && (
        <p className="text-danger mt-2">{trans(addQA.errors.result)}</p>
      )}
    </form>
  );
}
